import React, { useEffect, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, MenuItem, Stack, Tab, Table, TableBody, TableHead, Tabs, TextField, Toolbar, Tooltip, Typography,
} from "@mui/material";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import { useCursorList } from "../hooks/useCursorList";
import { useCan } from "../auth/Can";
import { getApiErrorMessage, toast } from "../lib/toast";
import LoadMoreBar from "../components/common/LoadMoreBar";
import { StyledPaper, StyledTableCell, StyledTableContainer, StyledTableRow } from "../assets/styles/tableStyle";
import {
    BotAnswerFilter, BotAnswerItem, BotStatus, getBotAnswers, getBotIntents, getBotStatus, intentLabel, postBotAnswerFeedback,
} from "../services/supportBotService";

/** Phase 4b/4c: one line on how far the bot is rolled out and whether the 👎 brake is on. */
export const statusLine = (s: BotStatus) => {
    const who = s.liveRoles.length === 2 ? "riders + drivers" : s.liveRoles.join(", ") || "nobody";
    const where = s.liveCities.length ? s.liveCities.join(", ") : "all cities";
    const brake = s.brake.engaged ? `BRAKE ON — ${s.brake.down} of ${s.brake.total} rated answers were 👎 (limit ${s.brake.maxDownPct}%)` : `brake off (${s.brake.down} 👎 of ${s.brake.total} rated)`;
    return `Live for ${s.livePercent}% of chats · ${who} · ${where} · ${s.liveIntents.length} topics + ${s.dataIntents.length} data topics · ${brake}`;
};

const TABS: { value: BotAnswerFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "shown", label: "Shown to customers" },
    { value: "shadow", label: "Not shown (shadow)" },
    { value: "unrated", label: "Not rated" },
    { value: "down", label: "👎 Rated poor" },
    { value: "possible_safety", label: "Possible safety" },
];

const when = (iso: string) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const RateCell: React.FC<{ row: BotAnswerItem; intents: string[]; onSaved: () => void }> = ({ row, intents, onSaved }) => {
    const [correct, setCorrect] = useState<string>(row.feedback?.correctIntent ?? "");
    const [saving, setSaving] = useState(false);
    const save = async (verdict: "up" | "down") => {
        setSaving(true);
        try {
            await postBotAnswerFeedback(row.id, { verdict, correctIntent: verdict === "down" && correct ? correct : null });
            toast({ message: verdict === "up" ? "Marked correct" : "Marked poor", severity: "success" });
            onSaved();
        } catch (e) {
            toast({ message: getApiErrorMessage(e, "Couldn't save the rating"), severity: "error" });
        } finally {
            setSaving(false);
        }
    };
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Bot was right"><span><Button size="small" disabled={saving} onClick={() => save("up")} startIcon={<ThumbUpAltOutlinedIcon />}>Good</Button></span></Tooltip>
            <TextField select size="small" label="Right topic" value={correct} onChange={(e) => setCorrect(e.target.value)} sx={{ minWidth: 190 }}>
                <MenuItem value="">—</MenuItem>
                {intents.map((i) => <MenuItem key={i} value={i}>{intentLabel(i)}</MenuItem>)}
            </TextField>
            <Tooltip title="Bot was wrong (pick the right topic first if you know it)"><span><Button size="small" color="error" disabled={saving} onClick={() => save("down")} startIcon={<ThumbDownAltOutlinedIcon />}>Poor</Button></span></Tooltip>
        </Stack>
    );
};

const BotAnswers: React.FC = () => {
    const { can } = useCan();
    const canRate = can("support:write");
    const [filter, setFilter] = useState<BotAnswerFilter>("all");
    const [intents, setIntents] = useState<string[]>([]);
    const [mode, setMode] = useState<string>("");

    const [status, setStatus] = useState<BotStatus | null>(null);
    useEffect(() => {
        getBotIntents().then((r) => { setIntents(r.data.intents); setMode(r.data.mode); }).catch(() => undefined);
        getBotStatus().then((r) => setStatus(r.data)).catch(() => undefined);
    }, []);

    const list = useCursorList<BotAnswerItem, string>({
        key: ["bot-answers", filter],
        pageSize: 30,
        meta: { suppressErrorToast: true },
        fetchPage: async ({ cursor, limit }) => {
            const res = await getBotAnswers({ filter, cursor: cursor ?? null, limit });
            return { items: res.data.items, nextCursor: res.data.nextCursor, hasMore: res.data.nextCursor != null };
        },
    });

    return (
        <StyledPaper>
            <Toolbar sx={{ gap: 2, flexWrap: "wrap" }}>
                <Typography variant="h6" sx={{ flex: 1 }}>Bot answers</Typography>
                {mode && <Chip label={`Bot mode: ${mode}`} color={mode === "live" ? "success" : mode === "shadow" ? "info" : "default"} />}
            </Toolbar>
            <Box sx={{ px: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Every customer message the support bot looked at. Rate its answer 👍 / 👎 and pick the right topic when it was wrong — these ratings train the next version.
                </Typography>
                {status?.mode === "live" && (
                    <Alert severity={status.brake.engaged ? "error" : "info"} sx={{ mt: 1 }} data-testid="bot-status">{statusLine(status)}</Alert>
                )}
            </Box>
            <Tabs value={filter} onChange={(_e, v) => setFilter(v)} variant="scrollable" sx={{ px: 2 }}>
                {TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
            </Tabs>
            {list.isError && <Alert severity="error" sx={{ m: 2 }}>{getApiErrorMessage(list.error, "Couldn't load bot answers")}</Alert>}
            {list.isLoading ? (
                <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
            ) : (
                <>
                    <StyledTableContainer>
                        <Table size="small">
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>When</StyledTableCell>
                                    <StyledTableCell>Customer message</StyledTableCell>
                                    <StyledTableCell>Bot decided</StyledTableCell>
                                    <StyledTableCell>Bot reply</StyledTableCell>
                                    <StyledTableCell>Rating</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {list.rows.length === 0 && (
                                    <StyledTableRow><StyledTableCell colSpan={5}><Typography color="text.secondary">Nothing here yet.</Typography></StyledTableCell></StyledTableRow>
                                )}
                                {list.rows.map((r) => (
                                    <StyledTableRow key={r.id}>
                                        <StyledTableCell sx={{ whiteSpace: "nowrap" }}>{when(r.createdAt)}</StyledTableCell>
                                        <StyledTableCell sx={{ maxWidth: 280 }}>{r.query}</StyledTableCell>
                                        <StyledTableCell>
                                            <Typography variant="body2" fontWeight={600}>{intentLabel(r.botIntent)}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {r.botAction}{r.botConfidence != null ? ` · ${Math.round(r.botConfidence * 100)}%` : ""}{r.botLatencyMs != null ? ` · ${r.botLatencyMs} ms` : ""}
                                            </Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                                <Chip size="small" label={r.shownToUser ? "Shown" : "Not shown"} color={r.shownToUser ? "success" : "default"} />
                                                {r.oldIntent && <Chip size="small" sx={{ ml: 0.5 }} label={`Old FAQ: ${r.oldIntent}`} variant="outlined" />}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ maxWidth: 320 }}>
                                            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{r.botReply ?? "—"}</Typography>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {r.feedback && (
                                                <Chip size="small" sx={{ mb: 1 }} color={r.feedback.verdict === "up" ? "success" : "error"}
                                                    label={r.feedback.verdict === "up" ? "👍 Good" : `👎 Poor${r.feedback.correctIntent ? ` → ${intentLabel(r.feedback.correctIntent)}` : ""}`} />
                                            )}
                                            {canRate ? <RateCell row={r} intents={intents} onSaved={() => list.refetch()} /> : !r.feedback && <Typography variant="caption">—</Typography>}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                    <LoadMoreBar count={list.rows.length} hasMore={list.hasMore} loading={list.isLoadingMore} onLoadMore={list.loadMore} />
                </>
            )}
        </StyledPaper>
    );
};

export default BotAnswers;
